import { CitationStyle } from '@/types/project';

export interface SourceAttribution {
  isAuthoredByUser: boolean;
  sourceAuthor?: string;
  sourcePublication?: string;
  sourcePublicationDate?: string;
  sourcePageNumbers?: string;
  sourceDoi?: string;
  sourceIsbn?: string;
  sourceUrl?: string;
}

export interface CitationData {
  author: string;
  title: string;
  publication?: string;
  year?: string;
  pageNumbers?: string;
  doi?: string;
  isbn?: string;
  url?: string;
  accessDate?: string;
}

// Format in-text citation based on style
export function formatInTextCitation(data: CitationData, style: CitationStyle): string {
  const { author, year, pageNumbers } = data;
  const lastName = author.split(' ').pop() || author;
  
  switch (style) {
    case 'APA':
      return pageNumbers 
        ? `(${lastName}, ${year || 'n.d.'}, p. ${pageNumbers})`
        : `(${lastName}, ${year || 'n.d.'})`;
    
    case 'MLA':
      return pageNumbers 
        ? `(${lastName} ${pageNumbers})`
        : `(${lastName})`;
    
    case 'Chicago-Author':
      return pageNumbers 
        ? `(${lastName} ${year || 'n.d.'}, ${pageNumbers})`
        : `(${lastName} ${year || 'n.d.'})`;
    
    case 'Chicago-Notes':
    case 'Turabian':
      return `[footnote]`; // Indicates footnote/endnote should be used
    
    case 'Harvard':
      return pageNumbers 
        ? `(${lastName}, ${year || 'n.d.'}: ${pageNumbers})`
        : `(${lastName}, ${year || 'n.d.'})`;
    
    case 'IEEE':
      return `[#]`; // Numeric citation
    
    case 'Vancouver':
    case 'AMA':
      return `(#)`; // Superscript numeric citation
    
    default:
      return '';
  }
}

// Format full bibliography entry based on style
export function formatBibliographyEntry(data: CitationData, style: CitationStyle): string {
  const { author, title, publication, year, pageNumbers, doi, url } = data;
  
  switch (style) {
    case 'APA': {
      let apa = `${author}. (${year || 'n.d.'}). ${title}.`;
      if (publication) apa += ` ${publication}.`;
      if (doi) apa += ` https://doi.org/${doi}`;
      else if (url) apa += ` Retrieved from ${url}`;
      return apa;
    }
    
    case 'MLA': {
      let mla = `${author}. "${title}."`;
      if (publication) mla += ` ${publication},`;
      if (year) mla += ` ${year}`;
      if (pageNumbers) mla += `, pp. ${pageNumbers}`;
      mla += '.';
      return mla;
    }
    
    case 'Chicago-Notes':
    case 'Turabian': {
      let chicago = `${author}, "${title}"`;
      if (publication) chicago += `, ${publication}`;
      if (year) chicago += ` (${year})`;
      if (pageNumbers) chicago += `: ${pageNumbers}`;
      chicago += '.';
      return chicago;
    }
    
    case 'Chicago-Author': {
      let chicagoAuthor = `${author}. ${year || 'n.d.'}. "${title}."`;
      if (publication) chicagoAuthor += ` ${publication}.`;
      return chicagoAuthor;
    }
    
    case 'Harvard': {
      let harvard = `${author} (${year || 'n.d.'}) '${title}'`;
      if (publication) harvard += `, ${publication}`;
      if (pageNumbers) harvard += `, pp. ${pageNumbers}`;
      harvard += '.';
      return harvard;
    }
    
    case 'IEEE': {
      let ieee = `${author}, "${title},"`;
      if (publication) ieee += ` ${publication},`;
      if (year) ieee += ` ${year}`;
      if (pageNumbers) ieee += `, pp. ${pageNumbers}`;
      ieee += '.';
      return ieee;
    }
    
    case 'Vancouver':
    case 'AMA': {
      let vancouver = `${author}. ${title}.`;
      if (publication) vancouver += ` ${publication}.`;
      if (year) vancouver += ` ${year}`;
      if (pageNumbers) vancouver += `;${pageNumbers}`;
      vancouver += '.';
      return vancouver;
    }
    
    default:
      return `${author}. ${title}.`;
  }
}

// Get citation style guidelines for AI prompts
export function getCitationStyleGuidelines(style: CitationStyle): string {
  switch (style) {
    case 'APA':
      return `**APA (7th Edition) Guidelines:**
- In-text: (Author, Year) or (Author, Year, p. X) for direct quotes
- Direct quotes under 40 words: Use "quotation marks" with (Author, Year, p. X)
- Direct quotes 40+ words: Block quote format, indented, no quotation marks
- Multiple authors: Use "&" in parentheses, "and" in text
- Reference list: Author, A. A. (Year). Title of work. Publisher. DOI/URL`;

    case 'MLA':
      return `**MLA (9th Edition) Guidelines:**
- In-text: (Author Page) - no comma
- Direct quotes: "quote text" (Author Page)
- Long quotes (4+ lines): Block indent, no quotation marks
- Works Cited: Author. "Title." Container, Other contributors, Version, Publisher, Date, Location.`;

    case 'Chicago-Notes':
    case 'Turabian':
      return `**Chicago Notes-Bibliography Guidelines:**
- Use footnotes (bottom of page) or endnotes (end of chapter/document)
- First citation: Full bibliographic information
- Subsequent citations: Author last name, shortened title, page number
- Direct quotes: Include in running text with footnote number after punctuation
- Bibliography: Full entries at end of work`;

    case 'Chicago-Author':
      return `**Chicago Author-Date Guidelines:**
- In-text: (Author Year, Page) 
- Direct quotes: Include page numbers
- Reference list: Author. Year. "Title." Publication details.`;

    case 'Harvard':
      return `**Harvard Referencing Guidelines:**
- In-text: (Author, Year: Page)
- Direct quotes: Single quotes for short quotes, block indent for long
- Reference list: Author (Year) Title, Publication, Pages.`;

    case 'IEEE':
      return `**IEEE Citation Guidelines:**
- Use numbered citations in square brackets [1]
- Citations numbered in order of appearance
- Direct quotes: "[quoted text]" [#]
- Reference list: Numbered entries in order of citation`;

    case 'Vancouver':
    case 'AMA':
      return `**Vancouver/Medical Style Guidelines:**
- Use superscript numbers for citations
- Number citations consecutively in order of first mention
- Direct quotes: Include page numbers
- Reference list: Numbered list in citation order`;

    default:
      return 'No specific citation format required.';
  }
}

// Get plagiarism prevention instructions for AI prompts
export function getPlagiarismPreventionInstructions(style: CitationStyle): string {
  const styleGuidelines = style !== 'none' ? getCitationStyleGuidelines(style) : '';
  
  return `## CRITICAL: Academic Integrity & Citation Rules

### Plagiarism Prevention (MANDATORY)
- **NEVER** copy text verbatim from research sources without proper quotation and citation
- **ALWAYS** paraphrase information using your own words and the author's chosen writing style
- When paraphrasing, completely restructure sentences - don't just swap synonyms
- Synthesize information from multiple sources when possible

### Using Research Sources
- Research items marked as "authored by user" or "my own content" can be used freely without citation
- ALL external sources require proper attribution when their ideas are used
- Prioritize the author's research over general knowledge when there's overlap

### Direct Quotes (Use Sparingly)
- Only quote directly when the original wording is essential or particularly eloquent
- Format quotes exactly according to the citation style
- Always include proper citation immediately after the quote
- Mark quoted text clearly so it can be tracked for bibliography

${styleGuidelines}

### Citation Markers
When citing sources in your output, use this format for tracking:
- For paraphrased content: Include citation naturally in text
- For direct quotes: Use "quoted text" followed by citation
- Reference source titles when they add credibility`;
}

// Map manuscript types to recommended citation styles
export const MANUSCRIPT_TYPE_CITATIONS: Record<string, CitationStyle> = {
  'how-to': 'none',
  'business': 'APA',
  'self-help': 'none',
  'memoir': 'Chicago-Notes',
  'biography': 'Chicago-Notes',
  'academic': 'APA',
  'research-paper': 'MLA',
  'thesis': 'APA',
  'scientific-paper': 'APA',
  'medical-paper': 'Vancouver',
  'legal': 'Chicago-Notes',
  'journalism': 'none',
  'thought-leadership': 'none',
  'textbook': 'APA',
  'essay-collection': 'MLA',
  'narrative-nonfiction': 'Chicago-Notes',
};

// Citation style display names
export const CITATION_STYLE_LABELS: Record<CitationStyle, string> = {
  'APA': 'APA (7th Edition)',
  'MLA': 'MLA (9th Edition)',
  'Chicago-Notes': 'Chicago Notes-Bibliography',
  'Chicago-Author': 'Chicago Author-Date',
  'Harvard': 'Harvard Referencing',
  'IEEE': 'IEEE',
  'Turabian': 'Turabian',
  'Vancouver': 'Vancouver/ICMJE',
  'AMA': 'AMA (American Medical)',
  'none': 'No Citations',
};

// Citation style info with name and description
export const CITATION_STYLE_INFO: Record<CitationStyle, { name: string; description: string }> = {
  'APA': { 
    name: 'APA (7th Edition)', 
    description: 'American Psychological Association - Common in social sciences' 
  },
  'MLA': { 
    name: 'MLA (9th Edition)', 
    description: 'Modern Language Association - Standard for humanities' 
  },
  'Chicago-Notes': { 
    name: 'Chicago Notes-Bibliography', 
    description: 'Footnote/endnote system - History, arts, literature' 
  },
  'Chicago-Author': { 
    name: 'Chicago Author-Date', 
    description: 'Parenthetical citations - Sciences, social sciences' 
  },
  'Harvard': { 
    name: 'Harvard Referencing', 
    description: 'Author-date system - UK and Australian universities' 
  },
  'IEEE': { 
    name: 'IEEE', 
    description: 'Numbered citations - Engineering and technology' 
  },
  'Turabian': { 
    name: 'Turabian', 
    description: 'Student version of Chicago - Academic papers' 
  },
  'Vancouver': { 
    name: 'Vancouver/ICMJE', 
    description: 'Numbered system - Medical and scientific journals' 
  },
  'AMA': { 
    name: 'AMA (American Medical)', 
    description: 'Superscript numbers - Medical publications' 
  },
  'none': { 
    name: 'No Citations', 
    description: 'No formal citation style required' 
  },
};
